// Direct Cognito auth composable — zero dependencies.
// Uses the Cognito Identity Provider REST API directly (no SDK, no AWS signature needed).
// Handles: login, NEW_PASSWORD_REQUIRED challenge, forgot password, logout.

// Pending challenge state: holds session + username across /login → /set-password navigation
interface PendingChallenge {
  session: string
  username: string
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function cognitoEndpoint(): string {
  // ap-southeast-1 extracted from user pool ID format: region_xxxxxxxx
  const config = useRuntimeConfig()
  const poolId = config.public.cognitoUserPoolId as string
  const region = poolId.split('_')[0] ?? 'ap-southeast-1'
  return `https://cognito-idp.${region}.amazonaws.com/`
}

async function cognitoPost(target: string, body: Record<string, unknown>): Promise<unknown> {
  const res = await fetch(cognitoEndpoint(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': `AWSCognitoIdentityProviderService.${target}`
    },
    body: JSON.stringify(body)
  })

  const data = await res.json() as Record<string, unknown>

  if (!res.ok) {
    const code = (data.__type as string | undefined)?.split('#').pop() ?? 'UnknownError'
    throw { code, message: (data.message as string | undefined) ?? 'An error occurred' } as AuthError
  }

  return data
}

function storeTokens(authResult: Record<string, string>): void {
  const accessToken = authResult.AccessToken ?? ''
  const idToken = authResult.IdToken ?? ''
  localStorage.setItem('access_token', accessToken)
  localStorage.setItem('id_token', idToken)
  // RefreshToken is only returned on initial auth, not on refresh — preserve existing if absent
  if (authResult.RefreshToken) {
    localStorage.setItem('refresh_token', authResult.RefreshToken)
  }
}

// ── Public API ────────────────────────────────────────────────────────────────

export type AuthError = { code: string, message: string }

/**
 * Standalone token refresh — safe to call from middleware.
 * Takes values resolved from useRuntimeConfig() BEFORE any await so the Nuxt
 * instance is not needed at call time.
 */
export async function tryRefreshTokens(endpoint: string, clientId: string): Promise<boolean> {
  const refreshToken = localStorage.getItem('refresh_token')
  if (!refreshToken) return false

  try {
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-amz-json-1.1',
        'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth'
      },
      body: JSON.stringify({
        AuthFlow: 'REFRESH_TOKEN_AUTH',
        ClientId: clientId,
        AuthParameters: { REFRESH_TOKEN: refreshToken }
      })
    })
    if (!res.ok) return false
    const data = await res.json() as { AuthenticationResult: Record<string, string> }
    storeTokens(data.AuthenticationResult)
    return true
  } catch {
    return false
  }
}

export function useAuth() {
  const config = useRuntimeConfig()
  const pendingChallenge = useState<PendingChallenge | null>('auth:pendingChallenge', () => null)

  async function refreshTokens(): Promise<boolean> {
    const refreshToken = localStorage.getItem('refresh_token')
    if (!refreshToken) return false

    try {
      const data = await cognitoPost('InitiateAuth', {
        AuthFlow: 'REFRESH_TOKEN_AUTH',
        ClientId: config.public.cognitoClientId,
        AuthParameters: {
          REFRESH_TOKEN: refreshToken
        }
      }) as Record<string, unknown>

      const authResult = data.AuthenticationResult as Record<string, string>
      storeTokens(authResult)
      return true
    } catch {
      return false
    }
  }

  /**
   * Sign in with email + password.
   * Returns 'ok' on success or 'new_password_required' when challenge is needed.
   * Throws AuthError on failure.
   */
  async function login(email: string, password: string): Promise<'ok' | 'new_password_required'> {
    const data = await cognitoPost('InitiateAuth', {
      AuthFlow: 'USER_PASSWORD_AUTH',
      ClientId: config.public.cognitoClientId,
      AuthParameters: {
        USERNAME: email,
        PASSWORD: password
      }
    }) as Record<string, unknown>

    if (data.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
      pendingChallenge.value = {
        session: data.Session as string,
        username: email
      }
      return 'new_password_required'
    }

    const authResult = data.AuthenticationResult as Record<string, string>
    storeTokens(authResult)
    return 'ok'
  }

  /**
   * Complete the NEW_PASSWORD_REQUIRED challenge.
   * Must be called after login() returns 'new_password_required'.
   */
  async function completeNewPassword(newPassword: string): Promise<'ok'> {
    const challenge = pendingChallenge.value
    if (!challenge) {
      throw { code: 'NoPendingChallenge', message: 'No pending password challenge. Please log in again.' } as AuthError
    }

    const data = await cognitoPost('RespondToAuthChallenge', {
      ChallengeName: 'NEW_PASSWORD_REQUIRED',
      ClientId: config.public.cognitoClientId,
      Session: challenge.session,
      ChallengeResponses: {
        USERNAME: challenge.username,
        NEW_PASSWORD: newPassword
      }
    }) as Record<string, unknown>

    const authResult = data.AuthenticationResult as Record<string, string>
    storeTokens(authResult)
    pendingChallenge.value = null
    return 'ok'
  }

  /**
   * Request a password reset code to be sent to the user's email.
   */
  async function forgotPassword(email: string): Promise<void> {
    await cognitoPost('ForgotPassword', {
      ClientId: config.public.cognitoClientId,
      Username: email
    })
  }

  /**
   * Confirm a password reset with the verification code sent by email.
   */
  async function confirmForgotPassword(email: string, code: string, newPassword: string): Promise<void> {
    await cognitoPost('ConfirmForgotPassword', {
      ClientId: config.public.cognitoClientId,
      Username: email,
      ConfirmationCode: code,
      Password: newPassword
    })
  }

  /**
   * Register a new user via the backend signup API.
   * Stores the email in sessionStorage for the /verify-email page.
   */
  async function signup(
    email: string,
    password: string,
    givenName: string,
    familyName: string,
    nickname: string
  ): Promise<void> {
    await $fetch('/api/auth/signup', {
      method: 'POST',
      body: { email, password, givenName, familyName, nickname }
    })
    sessionStorage.setItem('pending_verify_email', email)
  }

  /**
   * Confirm a new user's email address with the verification code sent by Cognito.
   */
  async function confirmSignup(email: string, code: string): Promise<void> {
    await cognitoPost('ConfirmSignUp', {
      ClientId: config.public.cognitoClientId,
      Username: email,
      ConfirmationCode: code
    })
  }

  /**
   * Resend the account confirmation code to the user's email.
   */
  async function resendConfirmationCode(email: string): Promise<void> {
    await cognitoPost('ResendConfirmationCode', {
      ClientId: config.public.cognitoClientId,
      Username: email
    })
  }

  /**
   * Sign out: clear local tokens and redirect to Cognito global sign-out.
   */
  function logout(): void {
    localStorage.removeItem('access_token')
    localStorage.removeItem('id_token')
    localStorage.removeItem('user_permissions')

    const { clearPermissions } = usePermissions()
    clearPermissions()

    const rawDomain = config.public.cognitoDomain as string
    if (rawDomain) {
      const domain = rawDomain.startsWith('https://') || rawDomain.startsWith('http://')
        ? rawDomain
        : `https://${rawDomain}`
      const params = new URLSearchParams({
        client_id: config.public.cognitoClientId as string,
        logout_uri: `${window.location.origin}/login`
      })
      window.location.href = `${domain}/logout?${params}`
    } else {
      navigateTo('/login')
    }
  }

  return { login, signup, confirmSignup, resendConfirmationCode, completeNewPassword, forgotPassword, confirmForgotPassword, logout, refreshTokens }
}
